import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { selectOpenedModalType } from '../../../../redux/selectors/modal';
import { ModalSliceType, openModal } from '../../../../redux/slices/modal';
import { useMutation, useQuery } from '@apollo/client';
import {
  IAnalysisSummaryInfoFieldsFragment,
  ICustomerPlanMtuReportQuery,
  ICustomerPlanMtuReportQueryVariables,
  ICustomerSetMtuReportMutation,
  ICustomerSetMtuReportMutationVariables,
} from '../../../../utils/apollo/graphqlSchema';
import {
  ASSIGN_CUSTOMER_PLAN_MTU_REPORT,
  GET_CUSTOMER_PLAN_MTU_REPORT,
} from '../../../../utils/apollo/globalConfig';
import { useCallback, useState } from 'react';
import toastMessage from '../../../../utils/toastMessage';
import { useAnalytics } from '../../../../utils/analytics/useAnalytics';
import useCustomerPlan from '../../../../utils/hooks/useCustomerPlan';
import { isNil } from 'lodash';
import { batch } from 'react-redux';
import { populateAnalysisThunk } from '../../../../redux/slices/analyses';
import { IAnalysisResponse } from '../../../../utils/apollo/responseTypes';
import { loadAnalysisAction } from '../../../../redux/slices/loadingAnalyses';
import { reportViewed } from '../../../../utils/analytics/events/report/events';
import printGraphqlErrors from '../../../../utils/apollo/printGraphqlErrors';
import { calculateTotal } from '../PlanUtils';
import { MaybeNil } from '../../../../utils/interfaces';

export const useCustomerPlanMTUReport = () => {
  const dispatch = useAppDispatch();
  const [analysisId, setAnalysisId] = useState<string>('');
  const [report, setReport] = useState<MaybeNil<IAnalysisResponse>>(null);
  const [total, setTotal] = useState<number>(0);
  const { track } = useAnalytics();
  const { mtuPlan: plan } = useCustomerPlan();

  const isModalOpen = useAppSelector((state) =>
    selectOpenedModalType(
      state,
      ModalSliceType.ReportsSearch,
      'select-mtu-report',
    ),
  );

  const loadAnalysisInStore = (analysis: IAnalysisResponse) => {
    if (!isNil(analysis)) {
      setAnalysisId(analysis.id);
      batch(() => {
        dispatch(
          populateAnalysisThunk({
            analysis,
          }),
        );
        dispatch(loadAnalysisAction(analysis.id));
      });
      track(reportViewed(analysis));
      setTotal(calculateTotal(analysis));
      setReport(analysis);
    }
  };

  const [assignReport, { loading: updating }] = useMutation<
    ICustomerSetMtuReportMutation,
    ICustomerSetMtuReportMutationVariables
  >(ASSIGN_CUSTOMER_PLAN_MTU_REPORT, {
    onCompleted: (data) => {
      if (printGraphqlErrors(data.customerSetMTUReport.errors)) {
        return;
      }
      if (data.customerSetMTUReport.analysis) {
        loadAnalysisInStore(data.customerSetMTUReport.analysis);
      }
    },
  });

  const { loading } = useQuery<
    ICustomerPlanMtuReportQuery,
    ICustomerPlanMtuReportQueryVariables
  >(GET_CUSTOMER_PLAN_MTU_REPORT, {
    onCompleted: (data) => {
      if (data.customerPlan?.mtuPlan?.mtuReport) {
        loadAnalysisInStore(data.customerPlan.mtuPlan.mtuReport);
      }
    },
    skip: !plan,
  });

  const openSearch = useCallback(() => {
    dispatch(
      openModal({
        type: ModalSliceType.ReportsSearch,
        data: {
          onSubmit: async (
            selectedReport: IAnalysisSummaryInfoFieldsFragment,
          ) => {
            if (selectedReport.__typename !== 'Expression') {
              toastMessage({
                message: 'Only query reports are allowed',
                type: 'error',
              });
              return;
            }
            await assignReport({
              variables: { analysisId: selectedReport.id },
            });
          },
        },
        id: 'select-mtu-report',
      }),
    );
  }, [assignReport, dispatch]);

  return {
    openSearch,
    isModalOpen,
    analysisId,
    report,
    plan,
    loading: updating || loading,
    total,
  };
};
